import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useDebounce } from 'use-debounce';
import type {
  GetMatchingStudyDescriptionsQuery,
  GetMatchingStudyDescriptionsQueryVariables,
} from 'generated/graphql';
import { GET_MATCHING_STUDY_DESCRIPTIONS } from 'modules/Apollo/queries';
import { CLASSIFIER_DEBOUNCE_DELAY } from 'config/constants';

/**
 * Hook to fetch matching study descriptions based on a study criteria source text.
 * Debounces the input and queries for matching descriptions.
 *
 * @param studyCriteriaSourceText - The study description text to search for matches
 * @returns Object containing the matching descriptions array and loading state
 */
export const useGetMatchingStudyDescriptions = (
  studyCriteriaSourceText: string
): { descriptions: ReadonlyArray<string>; loading: boolean } => {
  // Debounce study description for matching descriptions query
  const [debouncedStudyDescription] = useDebounce(
    studyCriteriaSourceText,
    CLASSIFIER_DEBOUNCE_DELAY
  );

  // Skip query if input is empty or whitespace-only
  const shouldSkipQuery = !debouncedStudyDescription?.trim();

  const { data: matchingDescriptionsData, loading: matchingDescriptionsLoading } = useQuery<
    GetMatchingStudyDescriptionsQuery,
    GetMatchingStudyDescriptionsQueryVariables
  >(GET_MATCHING_STUDY_DESCRIPTIONS, {
    variables: {
      studyDescription: debouncedStudyDescription.trim(),
    },
    fetchPolicy: 'cache-first',
    skip: shouldSkipQuery,
    context: {
      telemetry: {
        component: 'useGetMatchingStudyDescriptions',
        fetchPolicy: 'cache-first',
      },
    },
  });

  const matchingDescriptions = useMemo(
    () => matchingDescriptionsData?.getMatchingStudyDescriptions?.matchingStudyDescriptions ?? [],
    [matchingDescriptionsData]
  );

  return {
    descriptions: matchingDescriptions,
    loading: matchingDescriptionsLoading,
  };
};
